// The AI chat (`PlatformWeb.Layout.App.AssistantFrame`). Open/closed is `data-assistant-open` on
// <html>, saved to the user's preferences through `assistant_toggled`; where the floating panel
// was dragged to is `--app-assistant-floating-right` there, saved through `assistant_moved`.
import { isPhone, updateLayout as layout } from '../layout';

const root = document.documentElement;
const isFloating = () => root.getAttribute('data-assistant-mode') === 'floating';
// Below `sm` the floating panel covers the screen, so there's nowhere to drag it.
const canDrag = () => isFloating() && window.matchMedia('(min-width: 640px)').matches;
// The gap the panel keeps from the window's right edge, as its CSS does (1rem).
const EDGE = 16;

export default {
  AssistantPanel: {
    mounted() {
      this.panel = this.el.querySelector('[data-assistant-panel]');
      this.launcher = this.el.querySelector('[data-assistant-launcher]');

      this.onOpen = () => this.open();
      this.onClose = () => this.close();
      this.onKeyDown = e => this.handleKey(e);
      this.onPointerDown = e => this.startDrag(e);

      this.panel.addEventListener('pointerdown', this.onPointerDown);

      window.addEventListener('assistant:open', this.onOpen);
      window.addEventListener('assistant:close', this.onClose);
      window.addEventListener('keydown', this.onKeyDown);

      // Menus in the page header can't rise above it (a sticky z-40), so the floating panel drops
      // below the header while one is open.
      this.menuObserver = new MutationObserver(() =>
        root.toggleAttribute('data-menu-open', !!document.querySelector('[data-page-header] [aria-expanded="true"]')),
      );
      this.menuObserver.observe(document.body, { subtree: true, attributeFilter: ['aria-expanded'] });

      this.handleEvent('assistant:open', () => this.open());

      // A page restored from the browser cache can carry a stale <html>; the frame has the saved state.
      root.toggleAttribute('data-assistant-open', this.el.hasAttribute('data-open') && !isPhone());
      layout();
    },

    destroyed() {
      this.settling = null;
      this.stopDrag?.();
      window.removeEventListener('assistant:open', this.onOpen);
      window.removeEventListener('assistant:close', this.onClose);
      window.removeEventListener('keydown', this.onKeyDown);
      this.menuObserver.disconnect();
      root.removeAttribute('data-menu-open');
      requestAnimationFrame(layout);
    },

    isOpen() {
      return root.hasAttribute('data-assistant-open');
    },

    open() {
      root.setAttribute('data-assistant-open', '');
      layout();
      this.settle();
    },

    close() {
      const refocusLauncher = this.panel.contains(document.activeElement);
      root.removeAttribute('data-assistant-open');
      root.removeAttribute('data-sidebar-pinned');
      layout();
      this.settle(refocusLauncher);
    },

    // Save and focus once the panel's transitions end: doing either mid-animation stalls it.
    settle(refocusLauncher = false) {
      const settling = (this.settling = Symbol('settling'));

      Promise.allSettled(this.panel.getAnimations().map(animation => animation.finished)).then(() => {
        if (this.settling !== settling) return;

        this.pushEvent('assistant_toggled', { open: this.isOpen() });
        if (this.isOpen())
          this.panel.querySelector('textarea, [contenteditable="true"]')?.focus({ preventScroll: true });
        else if (refocusLauncher) this.launcher.focus({ preventScroll: true });
      });
    },

    // Drags the floating panel sideways by its header. Only the panel moves while dragging (an
    // inline `right`); on release the offset goes to <html>, where re-renders can't reset it.
    startDrag(e) {
      if (e.button !== 0 || !canDrag()) return;
      if (!e.target.closest('[data-assistant-drag]') || e.target.closest('button, a, input')) return;
      e.preventDefault();

      const panel = this.panel;
      const startX = e.clientX;
      const startRight = window.innerWidth - panel.getBoundingClientRect().right;
      // It goes no further left than the launcher, which sits just past the sidebar.
      const maxRight = window.innerWidth - panel.offsetWidth - this.launcher.offsetLeft;
      let right = null;

      const onMove = e => {
        right = Math.round(Math.min(Math.max(startRight + startX - e.clientX, EDGE), maxRight));
        panel.style.right = `${right}px`;
      };

      const onUp = () => {
        if (right !== null) {
          root.style.setProperty('--app-assistant-floating-right', `${right}px`);
          this.pushEvent('assistant_moved', { right });
        }
        this.stopDrag();
      };

      this.stopDrag = () => {
        document.removeEventListener('pointermove', onMove);
        document.removeEventListener('pointerup', onUp);
        document.removeEventListener('pointercancel', onUp);
        root.removeAttribute('data-dragging');
        panel.style.removeProperty('right');
        this.stopDrag = null;
      };

      root.setAttribute('data-dragging', '');
      document.addEventListener('pointermove', onMove);
      document.addEventListener('pointerup', onUp);
      document.addEventListener('pointercancel', onUp);
    },

    handleKey(e) {
      if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'j') {
        e.preventDefault();
        this.isOpen() ? this.close() : this.open();
      } else if (e.key === 'Escape' && isFloating() && this.isOpen() && !e.defaultPrevented) {
        // Menus inside the panel handle (and prevent) their own Escape first.
        if (this.panel.contains(document.activeElement) || document.activeElement === document.body) {
          this.close();
        }
      }
    },
  },
};
